import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { UserData } from "../types/user";

import { useNavigate, useSearchParams } from "react-router-dom";

import { userService } from "../services/userService";

import { ImageField, countries } from "../shared/formData";
import Header from "./layout/Header";
import { profileImageCache } from "../utils/imageCache";
import { HiChevronLeft as HiChevronLeftSolid } from "react-icons/hi";
import { HiLockClosed } from "react-icons/hi2";
import { useUserPrivacy } from "../stores";

const EditProfile: React.FC = () => {
  const { authState, currentUser, refreshUserData } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [localError, setLocalError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showGenderInfoDialog, setShowGenderInfoDialog] =
    useState<boolean>(false);

  // Privacy state
  const { isPrivate, updatePrivacy } = useUserPrivacy(currentUser?.uid || null);
  const [localPrivacyState, setLocalPrivacyState] = useState<boolean | null>(
    null,
  ); // Local state for optimistic UI

  // Form fields
  const [name, setName] = useState("");
  const [gender, setGender] = useState("");
  const [dob, setDob] = useState("");
  const [country, setCountry] = useState("");
  const [reason, setReason] = useState("");
  const [role, setRole] = useState("");
  const [isImperial, setIsImperial] = useState(false);
  const [autoPostTraining, setAutoPostTraining] = useState(false);
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const normalizeGender = (value?: string | null) => {
    const normalized = (value || "").trim().toLowerCase();
    if (normalized === "male" || normalized === "female") {
      return normalized;
    }
    return "";
  };

  // Effect to load existing user data
  useEffect(() => {
    if (authState.user) {
      const userData = authState.user;
      setName(userData.name);
      setGender(normalizeGender(userData.gender));
      setDob(userData.dob);
      setCountry(userData.country);
      setReason(userData.reason ?? "");
      setRole(userData.role || "");
      setIsImperial(userData.isImperial ?? false);
      setAutoPostTraining(userData.autoPostTraining ?? false);
      if (userData.imageUrlNormal || userData.imageUrl) {
        setImagePreview(userData.imageUrlNormal || userData.imageUrl || null);
      }
    }
  }, [authState.user]);

  // Effect to create image preview when file is selected
  useEffect(() => {
    if (profileImage) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(profileImage);
    }
  }, [profileImage]);

  // Sync local privacy state with store when it changes OR initialize from authState
  useEffect(() => {
    console.log("🔄 Privacy state sync effect triggered");
    console.log("isPrivate from store:", isPrivate);
    console.log(
      "authState.user?.isPrivateProfile:",
      authState.user?.isPrivateProfile,
    );
    console.log("localPrivacyState before sync:", localPrivacyState);

    // Initialize from store if available
    if (isPrivate !== null) {
      setLocalPrivacyState(isPrivate);
      console.log("✅ Local privacy state synced from store to:", isPrivate);
    }
    // Otherwise initialize from authState if available and local state is null
    else if (
      localPrivacyState === null &&
      authState.user?.isPrivateProfile !== undefined
    ) {
      setLocalPrivacyState(authState.user.isPrivateProfile);
      console.log(
        "✅ Local privacy state initialized from authState to:",
        authState.user.isPrivateProfile,
      );
    }
  }, [isPrivate, authState.user?.isPrivateProfile, localPrivacyState]);

  // Debug: Log privacy state on mount and when currentUser changes
  useEffect(() => {
    console.log("🔍 EditProfile Component Debug:");
    console.log("currentUser:", currentUser);
    console.log("authState.user:", authState.user);
    console.log(
      "authState.user?.isPrivateProfile:",
      authState.user?.isPrivateProfile,
    );
    console.log("isPrivate from store:", isPrivate);
    console.log("localPrivacyState:", localPrivacyState);
  }, [currentUser, authState.user, isPrivate, localPrivacyState]);

  // Redirect to home if not authenticated
  if (!authState.isAuthenticated) {
    navigate("/");
    return null;
  }

  const handlePrivacyToggle = async () => {
    console.log("🔄 handlePrivacyToggle called");
    console.log("Current User UID:", currentUser?.uid);
    console.log("isPrivate from store:", isPrivate);
    console.log("localPrivacyState:", localPrivacyState);
    console.log(
      "authState.user?.isPrivateProfile:",
      authState.user?.isPrivateProfile,
    );

    if (!currentUser?.uid) {
      console.error("❌ No currentUser.uid - aborting");
      return;
    }

    // Get current state (prefer local state, then store, then authState, default to false)
    const currentState =
      localPrivacyState !== null
        ? localPrivacyState
        : isPrivate !== null
          ? isPrivate
          : (authState.user?.isPrivateProfile ?? false);

    const newPrivacyValue = !currentState;

    console.log("Current state:", currentState);
    console.log("New privacy value:", newPrivacyValue);

    // Optimistic update: Update local state immediately for instant feedback
    setLocalPrivacyState(newPrivacyValue);
    console.log("✅ Local state updated to:", newPrivacyValue);

    try {
      console.log("🔄 Calling updatePrivacy...");
      await updatePrivacy(newPrivacyValue);
      console.log("✅ updatePrivacy completed successfully");

      console.log(`Profile is now ${newPrivacyValue ? "private" : "public"}`);

      // Refresh user data to ensure AuthContext is updated
      console.log("🔄 Refreshing user data...");
      await refreshUserData();
      console.log("✅ User data refreshed");
    } catch (error) {
      console.error("❌ Error updating privacy setting:", error);

      // Rollback optimistic update on error
      setLocalPrivacyState(currentState);
      console.log("⏮️ Rolled back to:", currentState);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setLoading(true);

    // Validation (dob is read-only and preserved as-is from the stored value)
    if (
      !name ||
      !gender ||
      !country
      // !reason ||
      // !role
    ) {
      setLocalError("Please fill in all required fields");
      setLoading(false);
      return;
    }

    try {
      if (!currentUser) {
        throw new Error("Not authenticated");
      }

      // Start with existing user data
      const updatedUserData: UserData = {
        ...authState.user!,
        name,
        gender: normalizeGender(gender),
        dob,
        country,
        reason,
        role,
        isImperial,
        autoPostTraining,
      };

      // Handle image upload if a new image was selected
      if (profileImage) {
        // Invalidate old cached image before uploading new one
        if (authState.user?.imageUrl) {
          profileImageCache.invalidateImage(authState.user.imageUrl);
        }

        const { imageUrl, imageUrlNormal, imageUrlTiny } =
          await userService.uploadProfileImage(currentUser.uid, profileImage);
        updatedUserData.imageUrl = imageUrl;
        updatedUserData.imageUrlNormal = imageUrlNormal;
        updatedUserData.imageUrlTiny = imageUrlTiny;
      }

      // Save to Firestore using userService
      await userService.updateUserProfile(currentUser.uid, updatedUserData);

      // Refresh AuthContext to get the updated profile data immediately
      await refreshUserData();

      // Check for query parameter and redirect accordingly
      const authParam = searchParams.get("auth");
      if (authParam === "requires-onboarding") {
        navigate("/onboarding");
      } else {
        // Navigate back to settings page
        navigate("/settings");
      }
    } catch (error) {
      setLocalError((error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full pt-16  min-h-screen pb-20">
      <Header />
      <div className="container max-w-lg mx-auto py-8 px-8">
        <div className="flex items-center pb-4">
          <button
            onClick={() => navigate("/settings")}
            className="flex items-center p-2 bg-base-300 rounded-full hover:bg-base-300 transition-colors"
          >
            <HiChevronLeftSolid className="h-6 w-6 text-base-content" />
          </button>
          <h1 className="text-3xl font-semibold text-base-content ml-4">
            Edit Profile
          </h1>
        </div>

        {localError && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-200 rounded-lg">
            <div className="text-red-700 dark:text-red-400 text-sm">
              {localError}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Profile Image */}
          <div className="text-center">
            <ImageField
              value={profileImage}
              previewUrl={imagePreview}
              onChange={setProfileImage}
              disabled={loading}
            />
          </div>

          <div className="grid grid-cols-1 gap-3">
            {/* Name */}
            <div className="form-control">
              <div className="new-form-group w-full">
                <input
                  type="text"
                  className="form-element"
                  placeholder="Enter your first name"
                  disabled={loading}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <label>First Name</label>
              </div>
            </div>

            {/* Gender */}
            <div className="form-control">
              <div className="flex items-center gap-2">
                <div className="new-form-group w-full">
                  <select
                    className="form-element"
                    disabled={loading}
                    value={gender}
                    onChange={(e) => setGender(normalizeGender(e.target.value))}
                  >
                    <option value=""></option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                  <label>Sex</label>
                  <div className="dropdown-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M6 9l6 6 6-6"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                    </svg>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowGenderInfoDialog(true)}
                  className="text-base-content/60 hover:text-base-content/80 transition-colors flex-shrink-0 mt-2"
                  aria-label="Why we ask for your sex"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Date of Birth (read-only — cannot be changed after registration) */}
            <div className="form-control">
              <div className="new-form-group w-full relative opacity-80">
                <input
                  type="text"
                  className="form-element cursor-not-allowed"
                  readOnly
                  disabled
                  value={(() => {
                    if (!dob) return "";
                    // Display stored YYYY-MM-DD as DD/MM/YYYY
                    if (/^\d{4}-\d{2}-\d{2}$/.test(dob)) {
                      const [yyyy, mm, dd] = dob.split("-");
                      return `${dd}/${mm}/${yyyy}`;
                    }
                    return dob;
                  })()}
                  style={{
                    maxWidth: "100%",
                    width: "100%",
                    fontSize: "16px",
                    paddingRight: "40px",
                  }}
                  title="Date of birth cannot be changed. Contact support if this is incorrect."
                  aria-readonly="true"
                />
                <label>Date of Birth</label>
                <HiLockClosed
                  aria-hidden="true"
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-base-content/50 pointer-events-none"
                />
              </div>
              <p className="text-xs text-base-content/60 mt-1 ml-1">
                Your date of birth can't be changed. Contact support if this is
                incorrect.
              </p>
            </div>

            {/* Country */}
            <div className="form-control">
              <div className="new-form-group w-full">
                <select
                  className="form-element"
                  disabled={loading}
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                >
                  <option value="">Select Country</option>
                  {countries.map((countryName) => (
                    <option key={countryName} value={countryName}>
                      {countryName}
                    </option>
                  ))}
                </select>
                <label>Country</label>
                <div className="dropdown-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M6 9l6 6 6-6"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {/* Measurement System */}
            <div className="form-control">
              <div className="new-form-group w-full">
                <select
                  className="form-element"
                  disabled={loading}
                  value={isImperial ? "true" : "false"}
                  onChange={(e) => setIsImperial(e.target.value === "true")}
                >
                  <option value="false">Metric (kg, km)</option>
                  <option value="true">Imperial (lbs, mi)</option>
                </select>
                <label>Measurement System</label>
                <div className="dropdown-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M6 9l6 6 6-6"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {/* Auto Post Training */}
            <div className="form-control">
              <label className="flex items-center gap-3 cursor-pointer bg-base-300 rounded-xl p-4">
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={autoPostTraining}
                  onChange={(e) => setAutoPostTraining(e.target.checked)}
                  disabled={loading}
                />
                <div className="flex-1">
                  <span className="text-base font-semibold text-base-content">
                    Auto Post Training
                  </span>
                  <p className="text-sm text-base-content/60">
                    Automatically share training logs as posts
                  </p>
                </div>
              </label>
            </div>

            {/* Private Profile */}
            <div className="form-control">
              <label className="flex items-center gap-3 cursor-pointer bg-base-300 rounded-xl p-4">
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={
                    localPrivacyState !== null
                      ? localPrivacyState
                      : isPrivate !== null
                        ? isPrivate
                        : authState.user?.isPrivateProfile || false
                  }
                  onChange={handlePrivacyToggle}
                  disabled={loading}
                />
                <div className="flex-1">
                  <span className="text-base font-semibold text-base-content">
                    Private Profile
                  </span>
                  <p className="text-sm text-base-content/60">
                    {(() => {
                      const currentPrivacyState =
                        localPrivacyState !== null
                          ? localPrivacyState
                          : isPrivate !== null
                            ? isPrivate
                            : authState.user?.isPrivateProfile;

                      return currentPrivacyState
                        ? "Private • No one can follow you"
                        : "Public • Anyone can follow you";
                    })()}
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Save Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-xl font-grotesk font-bold bg-primary text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <span className="loading loading-spinner"></span>
                Updating...
              </>
            ) : (
              "Update"
            )}
          </button>
        </form>
      </div>

      {/* Gender Info Dialog */}
      {showGenderInfoDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-base-100 rounded-lg shadow-xl max-w-md w-full p-8">
            <h3 className="text-lg font-bold font-grotesk text-base-content mb-4">
              Why we ask for your Sex?
            </h3>
            <p className="text-base-content/80 mb-6">
              PEAK^ uses biological sex to help interpret your physiological
              data accurately.
              <br />
              <br />
              By choosing an option that reflects your underlying physiology,
              you ensure your readiness scores and trends are correct.
              <br />
              <br />
              PEAK^ does not use this information for identification purposes or
              social features. It is stored securely and used solely for
              improving the accuracy of your insights.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowGenderInfoDialog(false)}
                className="px-4 py-2 text-sm font-grotesk text-white bg-neutral rounded-lg hover:bg-neutral transition-colors"
              >
                Okay, got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditProfile;
